// =================================================================================================
// >>> **WHEN A SCREEN THAT NEVER UNMOUNTS HAS TO READ AGAIN.** <<<
//
// **THIS HOUSE HAS ALREADY PAID FOR THIS ONCE, AND THE RECORD IS IN `App.tsx`'s OWN WORDS:**
//
//   *"On the web those are nearly the same thing. **In an installed PWA on an iPhone or iPad they
//    are not:** the app is resumed from the background for days without the React tree ever
//    unmounting... **AND IT SURVIVED EVERY REPAIR SHE WAS ASKED TO TRY**, which is why it was
//    reported four times and closed three: a pull-to-refresh inside a standalone PWA does not reload
//    the document, and signing out and back in does not remount a component this high in the tree.
//    Only force-quitting the app fixed it, and nobody thinks to ask that... Every instrument said
//    fresh. She was still right."*
//
// That was the daily guidance. **ITEM 3 found the identical shape on the MESSAGES INBOX**, reported
// by `mommymoney888@gmail.com` and by `arielletherealtor11@gmail.com` (#176/#177), and it is why two
// assurances were both true and both useless: the fixes shipped, and her app never read again.
//
// >>> **SO THE DECISION LIVES HERE, PURE, AND BOTH SURFACES ASK IT.** <<< Two copies of *should this
// screen read again* is two answers, and the one that drifts is the one on the surface nobody
// re-tested (R28/R29). It imports NOTHING but `import type`, which is what lets a `.mjs` gate LOAD
// and RUN every state of it with no browser (R146).
// =================================================================================================

/**
 * How long a read stays fresh before a resume is allowed to buy another one.
 *
 * **FIVE MINUTES IS THE DAILY GUIDANCE'S OWN NUMBER**, taken rather than re-chosen: a resume is a
 * common event and this throttle is what stops a member who glances at her phone twenty times an
 * hour from spending twenty reads. It is long enough to be a throttle and short enough that a day
 * cannot roll over behind it.
 */
export const RESUME_RE_READ_MS = 5 * 60 * 1000;

/**
 * **SHOULD THIS SCREEN READ AGAIN RIGHT NOW?**
 *
 * `visibility` is `document.visibilityState`. A resume fires this handler while the document is
 * still hidden on some platforms, and reading then buys a request for a screen nobody is looking at.
 *
 * `lastReadMs` of 0 means *never read*, which is not a stale read — it is a screen that has not
 * loaded yet, and its own mount effect owns that (R18). Answering true here would race it.
 */
export function shouldReReadOnResume({
  visibility = "visible",
  lastReadMs = 0,
  nowMs = Date.now(),
  afterMs = RESUME_RE_READ_MS,
}: {
  visibility?: string;
  lastReadMs?: number;
  nowMs?: number;
  afterMs?: number;
} = {}): boolean {
  if (visibility !== "visible") return false;
  // PRESENCE, never coercion: these arrive from a ref that a render may not have written yet.
  if (typeof lastReadMs !== "number" || !Number.isFinite(lastReadMs) || lastReadMs <= 0) return false;
  if (typeof nowMs !== "number" || !Number.isFinite(nowMs)) return false;
  const gap = typeof afterMs === "number" && Number.isFinite(afterMs) && afterMs >= 0 ? afterMs : RESUME_RE_READ_MS;
  return nowMs - lastReadMs >= gap;
}

/**
 * >>> **THE TWO EVENTS A RESUME CAN ARRIVE ON, DECLARED RATHER THAN CHOSEN AT A CALL SITE.** <<<
 *
 * The daily guidance's own measured ruling, in its own words: *"iOS standalone reliably fires
 * `visibilitychange`; a desktop tab returning from another window fires `focus`. Listening for one
 * of them is how this would go on being broken for half the members who reported it."*
 *
 * It is a roster so a surface cannot join with one of the two and look correct in every diff.
 */
export const RESUME_EVENTS = ["visibilitychange", "focus"] as const;
